import React, { Component, ReactNode } from 'react'
import { css, cx } from '@emotion/css'
import Downshift from 'downshift'
import { monotonic } from '../../util'
import { CsCloseIcon } from '../icons'
import {
  textBoxBaseStyle,
  textBoxBorderStyle,
  textBoxFocusedBorderStyle,
  textBoxBorderRule
} from '../form-text-box'
import {
  fnOrVal,
  comboboxInputValueID,
  comboboxInputValueRemoveID,
  comboboxInputID,
  ComboboxItem,
  comboboxMenuItemID
} from './shared'
import { ComboBoxMenu } from './combobox-menu'
import { Label } from '../form-label'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { FormFieldProps, Validatable } from '../form/form-validator'
import { FormTextContainer } from '../form-text'
import { MessageDescriptor } from 'react-intl'

interface Item {
  appId?: string
  value?: any
}

interface InputWrapperProps {
  readonly menuIsOpen: boolean
  readonly isFocused: boolean
  readonly children?: React.ReactNode
  readonly disabled?: boolean
}

function InputWrapper({
  menuIsOpen,
  isFocused,
  disabled,
  ...props
}: InputWrapperProps) {
  return (
    <div
      {...props}
      className={cx(
        'legacy-ds-combobox-input-wrapper',
        textBoxBaseStyle,
        textBoxBorderStyle,
        (menuIsOpen || isFocused) && textBoxFocusedBorderStyle,
        menuIsOpen &&
          css`
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
            border-bottom: ${textBoxBorderRule};

            &:focus {
              border-bottom: ${textBoxBorderRule};
            }
          `,
        css`
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          height: auto;
        `,
        disabled &&
          css`
            border: 1px solid ${theme.color.neutral40};
            background-color: ${theme.color.neutral20};
          `
      )}
    />
  )
}

function Input(props) {
  const { inputRef, errorMessage: _errorMessage, ...rest } = props

  return (
    <input
      {...rest}
      ref={inputRef}
      className={css`
        flex: 1 0 3em;
        min-width: 0;
        padding: 0;
        outline: none;
        background: transparent;
        border: none;
      `}
    />
  )
}

function SelectedValue({ disabled = false, ...props }) {
  return (
    <div
      {...props}
      className={cx(
        'legacy-ds-combobox-selected-value',
        css`
          display: inline-flex;
          align-items: center;
          flex: 0 0 auto;
          font-weight: 400;
          text-align: center;
          vertical-align: middle;
          border: 1px solid ${disabled ? theme.color.neutral40 : '#ededed'};
          white-space: nowrap;
          border-radius: 3px;
          color: ${disabled ? theme.color.neutral60 : '#3a3a3a'};
          transition: background-color 0.4s, border-color 0.4s;
          padding: 1px 4px;
          margin: 2px 6px 2px 0;
          font-size: 12px;
          background-color: ${disabled ? theme.color.neutral20 : '#fafafa'};
          line-height: 1.5;

          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;

          &:hover {
            background-color: whitesmoke;
          }
        `
      )}
    />
  )
}

interface Props<T> {
  readonly 'aria-labelledby'?: string

  /**
   * Toggles an EDITED indicator on the field
   */
  readonly edited?: boolean

  /**
   * The id of the input element. Note: this should always be provided in tests
   */
  readonly id?: string

  readonly customComboboxInputTestID?: string

  readonly createMessage?: MessageDescriptor
  readonly isAddAllowed?: boolean
  readonly items: Array<ComboboxItem<T>>
  readonly itemsEqual?: (first: T, second: T) => boolean
  readonly itemFromInput?: (inputValue: string) => T
  readonly itemToString?: (item: T) => string
  readonly itemToID?: (item: T) => string
  readonly getFormFieldProps?: FormFieldProps<Validatable>

  /**
   * Text describing the input element. This will be used as the content
   * for the label element unless the renderLabel prop is used
   */
  readonly label?: string

  readonly onSearchChange?: (value) => void
  readonly onChange: (values: T[]) => void
  readonly placeHolderTag?: string
  readonly placeHolderText?: string
  readonly onDropdownVisibilityChanged?: (value: boolean) => void

  /**
   * If provided this callback will render the contents of a help tooltip. Also,
   * a help icon will appear as part of the control's label that activates the
   * tooltip when hovered.
   */
  readonly renderHelpContent?: () => ReactNode

  /**
   * Can be used to render an alternate label or exclude the label element
   * altogether if the callback returns null/undefined. If this callback is
   * defined it will be passed the value of the label prop as the first argument
   * and the input element id as the second argument.
   */
  readonly renderLabel?: (string, id) => ReactNode

  readonly values: T[]

  readonly onNewItemClick?: (inputValue: string) => void

  /**
   * The text to display if there are no results in the dropdown
   */
  readonly noResultsText?: string

  readonly disabled?: boolean

  /* Optionally provide an alternative translation ID for when an item already exists */
  readonly tagExistsTranslationID?: string
}

interface State {
  readonly inputValue: string
  readonly focus: number
  readonly blur: number
  readonly activationEvent: number
  readonly deactivationEvent: number
}

export class MultiSelectCombobox<T> extends Component<Props<T>, State> {
  public readonly state: State = {
    inputValue: '',
    focus: 0,
    blur: 0,
    activationEvent: 0,
    deactivationEvent: 0
  }

  private nextInterval = monotonic()
  private inputRef: HTMLInputElement

  public componentDidUpdate(_prevProps, prevState) {
    const currentOpenState =
      this.state.activationEvent > this.state.deactivationEvent
    const prevOpenState =
      prevState.activationEvent > prevState.deactivationEvent
    if (currentOpenState !== prevOpenState) {
      this.props.onDropdownVisibilityChanged?.(currentOpenState)
    }
  }

  public render() {
    const {
      edited,
      values,
      id,
      items,
      itemToString,
      itemsEqual,
      itemToID,
      label,
      placeHolderTag,
      placeHolderText,
      renderHelpContent,
      renderLabel,
      isAddAllowed,
      itemFromInput,
      createMessage,
      onNewItemClick,
      noResultsText,
      disabled,
      tagExistsTranslationID,
      getFormFieldProps,
      customComboboxInputTestID,
      onSearchChange: _onSearchChange,
      onDropdownVisibilityChanged: _onDropdownVisibilityChanged,
      ...props
    } = this.props

    const { inputValue, focus, blur, activationEvent, deactivationEvent } =
      this.state

    // The menu is open when an activation event has happened
    // more recently than a deactivation event
    const isOpen = activationEvent > deactivationEvent

    const labelNode = renderLabel
      ? renderLabel(label, id)
      : label && (
          <Label
            edited={edited}
            htmlFor={id}
            renderHelpContent={renderHelpContent}
          >
            {label}
          </Label>
        )

    return (
      <Downshift
        inputValue={inputValue}
        onChange={this.handleChange}
        selectedItem={values}
        defaultHighlightedIndex={0}
        itemToString={() => ''}
        onOuterClick={() =>
          this.setState({
            inputValue: '',
            deactivationEvent: this.nextInterval()
          })
        }
        isOpen={isOpen}
        onSelect={() => this.inputRef && this.inputRef.focus()}
        onStateChange={(changes: any) =>
          // TODO: deactivating on the text input's blur event would cause
          // the menu to close when clicking on an item. Instead we want
          // to deactivate on the Downshift state change type that corresponds
          // to the entire contol (input and menu) being blurred. Unfortunately,
          // in our version of Downshift the property `type` on the `changes`
          // parameter is undocumented. When we upgrade we should be able to
          // remove the any annotation on this callback's parameter.
          changes.type === '__autocomplete_blur_input__' &&
          this.setState({ deactivationEvent: this.nextInterval() })
        }
      >
        {({ getInputProps, getItemProps, highlightedIndex }) => (
          <div
            data-e2e="multi-select-combobox-row"
            className={css`
              display: flex;
              flex-direction: column;
              width: 100%;
              position: relative;
            `}
          >
            <FormTextContainer
              disabled={disabled}
              {...getFormFieldProps}
              {...props}
            >
              {({
                renderLabel,
                id,
                label,
                onChange: _onChange,
                value: _value,
                onBlur,
                onFocus,
                ...props
              }) => (
                <div>
                  {labelNode}
                  <div
                    className={css`
                      ${disabled && 'pointer-events: none;'} position: relative;
                    `}
                  >
                    <InputWrapper
                      menuIsOpen={isOpen}
                      isFocused={focus > blur}
                      disabled={disabled}
                    >
                      {values.length > 0
                        ? values.map((val, index) => (
                            <SelectedValue
                              data-testid={comboboxInputValueID}
                              disabled={disabled}
                              key={fnOrVal(val, itemToID, val)}
                            >
                              {disabled ? (
                                fnOrVal(val, itemToString, val)
                              ) : (
                                <React.Fragment>
                                  <span
                                    className={css`
                                      overflow: hidden;
                                      text-overflow: ellipsis;
                                    `}
                                  >
                                    {fnOrVal(val, itemToString, val)}
                                  </span>
                                  <CsCloseIcon
                                    className={css`
                                      margin-left: 4px;
                                    `}
                                    data-testid={comboboxInputValueRemoveID}
                                    data-e2e={fnOrVal(val, itemToID, val)}
                                    onClick={() => this.handleRemove(index)}
                                  />
                                </React.Fragment>
                              )}
                            </SelectedValue>
                          ))
                        : placeHolderTag && (
                            <SelectedValue disabled={disabled}>
                              {placeHolderTag}
                            </SelectedValue>
                          )}
                      <Input
                        inputRef={(ref) => (this.inputRef = ref)}
                        placeholder={
                          values.length === 0 ? placeHolderText : undefined
                        }
                        data-testid={
                          customComboboxInputTestID || comboboxInputID
                        }
                        data-e2e="combobox-input-field"
                        {...getInputProps({
                          'aria-labelledby': props['aria-labelledby'],
                          // provide aria-label when we can't ensure a label is associated
                          // with the control
                          'aria-label': renderLabel || !id ? label : undefined,
                          onChange: this.handleInputChange,
                          onKeyDown: this.handleKeyDown,
                          onBlur: this.handleBlur,
                          onFocus: this.handleFocus
                        })}
                        onFocus={() => {
                          this.handleFocus()

                          if (onFocus) {
                            onFocus()
                          }
                        }}
                        onBlur={(event) => {
                          this.handleBlur()
                          if (onBlur) {
                            onBlur(event)
                          }
                        }}
                        {...props}
                      />
                    </InputWrapper>
                    {isOpen && (
                      <ComboBoxMenu
                        values={values}
                        items={items}
                        itemToString={itemToString}
                        itemsEqual={itemsEqual}
                        itemToID={itemToID}
                        getItemProps={getItemProps}
                        highlightedIndex={highlightedIndex}
                        inputValue={inputValue}
                        isAddAllowed={isAddAllowed}
                        itemFromInput={itemFromInput}
                        createMessage={createMessage}
                        onNewItemClick={onNewItemClick}
                        noResultsText={noResultsText}
                        comboboxMenuItemToTestID={(item) => {
                          if (itemToID) {
                            return `${itemToID(item)}-menu-item`
                          }
                          return comboboxMenuItemID
                        }}
                        tagExistsTranslationID={tagExistsTranslationID}
                      />
                    )}
                  </div>
                </div>
              )}
            </FormTextContainer>
          </div>
        )}
      </Downshift>
    )
  }

  private handleBlur = () => {
    this.setState({
      blur: this.nextInterval()
    })
  }

  private handleFocus = () => {
    this.setState({
      focus: this.nextInterval(),
      activationEvent: this.nextInterval()
    })
  }

  handleChange = (value) => {
    if (value) {
      const { values, onChange } = this.props

      if (!this.hasDuplicates(values, value)) {
        onChange([...values, value])
        this.setState({ inputValue: '' })
      }
    }
  }

  hasDuplicates<T extends Item>(values: T[], value: T): boolean {
    return values.some(
      (item) =>
        (item.appId && item.appId === value.appId) ||
        (item.value && item.value === value.value)
    )
  }

  private handleRemove = (index) => {
    const { values, onChange } = this.props
    onChange([...values.slice(0, index), ...values.slice(index + 1)])
  }

  private handleKeyDown = (e) => {
    const { values } = this.props

    switch (e.keyCode) {
      // Backspace
      case 8:
        if (values.length && !this.state.inputValue.length) {
          this.props.onChange(values.slice(0, values.length - 1))
        }
        break
      // Escape
      case 27:
        this.setState({
          deactivationEvent: this.nextInterval(),
          inputValue: ''
        })
        this.props.onSearchChange?.('')
        break
      // Down Arrow
      case 40:
        this.setState({ activationEvent: this.nextInterval() })
        break
      // Tab
      case 9:
        this.setState({
          deactivationEvent: this.nextInterval()
        })
        break
      default:
        return
    }
  }

  private handleInputChange = (e) => {
    this.props.onSearchChange?.(e.target.value)
    this.setState({
      inputValue: e.target.value,
      activationEvent: this.nextInterval()
    })
  }
}
